import { useState, useRef } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, CheckCircle, XCircle, AlertTriangle, Loader2, Trash2, RefreshCw, History, Play } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface XmlValidado {
  xmlParsed: {
    chaveAcesso: string;
    numeroNota: string;
    serie: string;
    dataEmissao: string;
    valorTotal: number;
    cnpjEmitente: string;
    nomeEmitente: string;
    cnpjDestinatario: string;
    nomeDestinatario: string;
    cStat: string;
    itens: Array<{
      cProd: string;
      xProd: string;
      qCom: number;
      vUnCom: number;
      vProd: number;
      NCM: string;
      CFOP: string;
      CST_ICMS: string;
      vICMS: number;
      pICMS: number;
      vIPI: number;
      pIPI: number;
      vPIS: number;
      pPIS: number;
      vCOFINS: number;
      pCOFINS: number;
    }>;
    xmlOriginal: string;
  };
  valido: boolean;
  erros: string[];
}

export default function QuoteXml() {
  const { user } = useGoogleAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados
  const [activeTab, setActiveTab] = useState<string>("importacao");
  const [etapa, setEtapa] = useState<"upload" | "validacao" | "importando" | "concluido">("upload");
  const [emitenteId, setEmitenteId] = useState<string>("");
  const [xmlsRaw, setXmlsRaw] = useState<string[]>([]);
  const [resultadosValidacao, setResultadosValidacao] = useState<XmlValidado[]>([]);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [processando, setProcessando] = useState(false);
  const [progressoLote, setProgressoLote] = useState<{ loteAtual: number; totalLotes: number; pedidosCriados: number; errosAcumulados: string[] } | null>(null);
  const [emitindoRetornos, setEmitindoRetornos] = useState(false);
  const [progressoRetornos, setProgressoRetornos] = useState<{ emitidos: number; erros: number; restantes: number } | null>(null);

  // Queries
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  // Pendências
  const { data: pendencias = [], refetch: refetchPendencias } = trpc.importacaoXml.listarPendencias.useQuery(
    { emitenteId: Number(emitenteId), status: "pendente" },
    { enabled: !!emitenteId }
  );

  // Histórico
  const { data: historico = [], refetch: refetchHistorico } = trpc.importacaoXml.historico.useQuery(
    { emitenteId: Number(emitenteId) },
    { enabled: !!emitenteId }
  );

  // Mutations
  const validarMutation = trpc.importacaoXml.validar.useMutation({
    onSuccess: (data) => {
      setResultadosValidacao(data as XmlValidado[]);
      // Selecionar todos os válidos por padrão
      const validosIdx = new Set<number>();
      (data as XmlValidado[]).forEach((r, i) => {
        if (r.valido) validosIdx.add(i);
      });
      setSelecionados(validosIdx);
      setEtapa("validacao");
    },
    onError: (err) => {
      toast.error(`Erro na validação: ${err.message}`);
    },
  });

  const processarMutation = trpc.importacaoXml.processar.useMutation();

  const emitirRetornosMutation = trpc.importacaoXml.emitirRetornos.useMutation();

  const salvarPendenciasMutation = trpc.importacaoXml.salvarPendencias.useMutation({
    onSuccess: () => {
      toast.success("Pendências salvas com sucesso!");
      refetchPendencias();
    },
    onError: (err) => {
      toast.error(`Erro ao salvar pendências: ${err.message}`);
    },
  });

  const descartarPendenciaMutation = trpc.importacaoXml.descartarPendencia.useMutation({
    onSuccess: () => {
      toast.success("Pendência descartada.");
      refetchPendencias();
    },
    onError: (err) => {
      toast.error(`Erro: ${err.message}`);
    },
  });

  const reprocessarPendenciaMutation = trpc.importacaoXml.reprocessarPendencia.useMutation({
    onSuccess: (data: any) => {
      if (data.sucesso) {
        toast.success("Pendência reprocessada com sucesso!");
      } else {
        toast.error(`Falha ao reprocessar: ${data.erro}`);
      }
      refetchPendencias();
      refetchHistorico();
    },
    onError: (err) => {
      toast.error(`Erro: ${err.message}`);
    },
  });

  // Handlers
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const xmlTexts: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const text = await file.text();
      xmlTexts.push(text);
    }
    setXmlsRaw(xmlTexts);
    toast.success(`${xmlTexts.length} arquivo(s) carregado(s).`);
  };

  const handleAnalisar = () => {
    if (!emitenteId) {
      toast.error("Selecione um emitente.");
      return;
    }
    if (xmlsRaw.length === 0) {
      toast.error("Nenhum arquivo XML carregado.");
      return;
    }
    validarMutation.mutate({ xmls: xmlsRaw, emitenteId: Number(emitenteId) });
  };

  const LOTE_SIZE = 30;

  const processarEmLotes = async (xmlsValidos: string[]) => {
    const lotes: string[][] = [];
    for (let i = 0; i < xmlsValidos.length; i += LOTE_SIZE) {
      lotes.push(xmlsValidos.slice(i, i + LOTE_SIZE));
    }

    setProcessando(true);
    setEtapa("importando");
    setProgressoLote({ loteAtual: 0, totalLotes: lotes.length, pedidosCriados: 0, errosAcumulados: [] });

    let totalCriados = 0;
    const todosErros: string[] = [];

    for (let i = 0; i < lotes.length; i++) {
      setProgressoLote({ loteAtual: i + 1, totalLotes: lotes.length, pedidosCriados: totalCriados, errosAcumulados: todosErros });
      try {
        const result: any = await processarMutation.mutateAsync({
          xmls: lotes[i],
          emitenteId: Number(emitenteId),
        });
        totalCriados += result.processados || 0;
        if (result.erros) todosErros.push(...result.erros);
      } catch (err: any) {
        todosErros.push(`Erro no lote ${i + 1}: ${err.message}`);
      }
    }

    setProgressoLote({ loteAtual: lotes.length, totalLotes: lotes.length, pedidosCriados: totalCriados, errosAcumulados: todosErros });
    toast.success(`${totalCriados} pedido(s) criado(s) com status aguardando retorno.`);
    if (todosErros.length > 0) {
      toast.error(`${todosErros.length} erro(s) durante o processamento.`);
    }
    setProcessando(false);
    setEtapa("concluido");
    refetchHistorico();
  };

  const handleImportarCorretos = () => {
    const validos = resultadosValidacao
      .filter((_, i) => selecionados.has(i))
      .filter((r) => r.valido);

    if (validos.length === 0) {
      toast.error("Nenhum XML válido selecionado.");
      return;
    }

    processarEmLotes(validos.map((r) => r.xmlParsed.xmlOriginal));
  };

  const handleImportarCorretosESalvarPendencias = () => {
    const validos = resultadosValidacao
      .filter((_, i) => selecionados.has(i))
      .filter((r) => r.valido);

    const invalidos = resultadosValidacao.filter((r) => !r.valido);

    if (validos.length === 0 && invalidos.length === 0) {
      toast.error("Nenhum XML para processar.");
      return;
    }

    // Salvar pendências primeiro
    if (invalidos.length > 0) {
      salvarPendenciasMutation.mutate({
        pendencias: invalidos.map((r) => ({
          xml: r.xmlParsed.xmlOriginal,
          erros: r.erros,
        })),
        emitenteId: Number(emitenteId),
      });
    }

    // Processar válidos em lotes
    if (validos.length > 0) {
      processarEmLotes(validos.map((r) => r.xmlParsed.xmlOriginal));
    }
  };

  const handleToggleSelecionado = (idx: number) => {
    const novo = new Set(selecionados);
    if (novo.has(idx)) {
      novo.delete(idx);
    } else {
      novo.add(idx);
    }
    setSelecionados(novo);
  };

  const handleRemoverLinha = (idx: number) => {
    const novosResultados = resultadosValidacao.filter((_, i) => i !== idx);
    setResultadosValidacao(novosResultados);
    const novosSelecionados = new Set<number>();
    selecionados.forEach((i) => {
      if (i < idx) novosSelecionados.add(i);
      else if (i > idx) novosSelecionados.add(i - 1);
    });
    setSelecionados(novosSelecionados);
  };

  const handleEmitirRetornos = async () => {
    setEmitindoRetornos(true);
    setProgressoRetornos({ emitidos: 0, erros: 0, restantes: 0 });

    let totalEmitidos = 0;
    let totalErros = 0;

    while (true) {
      try {
        const result: any = await emitirRetornosMutation.mutateAsync({
          emitenteId: Number(emitenteId),
          loteSize: LOTE_SIZE,
        });
        totalEmitidos += result.emitidos || 0;
        totalErros += result.erros?.length || 0;
        setProgressoRetornos({ emitidos: totalEmitidos, erros: totalErros, restantes: result.restantes || 0 });

        if ((result.restantes || 0) === 0) break;
      } catch (err: any) {
        toast.error(`Erro ao emitir retornos: ${err.message}`);
        break;
      }
    }

    toast.success(`${totalEmitidos} retorno(s) emitido(s).`);
    if (totalErros > 0) toast.error(`${totalErros} erro(s) na emissão.`);
    setEmitindoRetornos(false);
    refetchHistorico();
  };

  const handleReset = () => {
    setEtapa("upload");
    setXmlsRaw([]);
    setResultadosValidacao([]);
    setSelecionados(new Set());
    setProcessando(false);
    setProgressoLote(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Contadores
  const totalValidos = resultadosValidacao.filter((r) => r.valido).length;
  const totalInvalidos = resultadosValidacao.filter((r) => !r.valido).length;
  const pedidosAguardandoRetorno = (historico as any[]).filter((h: any) => h.status === "aguardando_retorno").length;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Orçamento XML NFe</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="importacao">Importação</TabsTrigger>
          <TabsTrigger value="pendencias">Pendências</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        {/* ═══ ABA IMPORTAÇÃO ═══ */}
        <TabsContent value="importacao">
          <Card>
            <CardHeader>
              <CardTitle>Importar XMLs de NF-e</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Seleção de emitente */}
              <div className="flex items-center gap-4">
                <label className="font-medium">Emitente:</label>
                <Select value={emitenteId} onValueChange={setEmitenteId}>
                  <SelectTrigger className="w-[300px]">
                    <SelectValue placeholder="Selecione o emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    {emitentes.map((em: any) => (
                      <SelectItem key={em.id} value={String(em.id)}>
                        {em.razaoSocial} ({em.cnpj})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Upload */}
              {etapa === "upload" && (
                <div className="space-y-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xml"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex items-center gap-4">
                    <Button onClick={() => fileInputRef.current?.click()} variant="outline">
                      <Upload className="w-4 h-4 mr-2" />
                      Selecionar XMLs
                    </Button>
                    {xmlsRaw.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {xmlsRaw.length} arquivo(s) selecionado(s)
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={handleAnalisar}
                    disabled={xmlsRaw.length === 0 || !emitenteId || validarMutation.isPending}
                  >
                    {validarMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisando...</>
                    ) : (
                      "Analisar XMLs"
                    )}
                  </Button>
                </div>
              )}

              {/* Validação */}
              {etapa === "validacao" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-600" /> {totalValidos} válido(s)
                    </span>
                    <span className="flex items-center gap-1">
                      <XCircle className="w-4 h-4 text-red-600" /> {totalInvalidos} com pendência(s)
                    </span>
                  </div>

                  <div className="border rounded-md overflow-auto max-h-[60vh]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead>Nº Nota</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead>NF-e Autorizada</TableHead>
                          <TableHead>CNPJ Emitente</TableHead>
                          <TableHead>Produtos OK</TableHead>
                          <TableHead>Estoque OK</TableHead>
                          <TableHead>Valor Total</TableHead>
                          <TableHead>Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resultadosValidacao.map((r, idx) => (
                          <TableRow key={idx} className={!r.valido ? "bg-red-50" : ""}>
                            <TableCell>
                              <Checkbox
                                checked={selecionados.has(idx)}
                                onCheckedChange={() => handleToggleSelecionado(idx)}
                              />
                            </TableCell>
                            <TableCell>{r.xmlParsed.numeroNota || "-"}</TableCell>
                            <TableCell>{r.xmlParsed.dataEmissao || "-"}</TableCell>
                            <TableCell>
                              {r.xmlParsed.cStat === "100" ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-600" />
                              )}
                            </TableCell>
                            <TableCell>
                              {r.erros.some((e) => e.includes("CNPJ")) ? (
                                <XCircle className="w-4 h-4 text-red-600" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              )}
                            </TableCell>
                            <TableCell>
                              {r.erros.some((e) => e.includes("produto") || e.includes("Produto")) ? (
                                <span className="flex items-center gap-1">
                                  <XCircle className="w-4 h-4 text-red-600" />
                                  <span className="text-xs text-red-600" title={r.erros.filter((e) => e.includes("roduto")).join(", ")}>
                                    Ver erros
                                  </span>
                                </span>
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              )}
                            </TableCell>
                            <TableCell>
                              {r.erros.some((e) => e.includes("estoque") || e.includes("Estoque") || e.includes("saldo")) ? (
                                <span className="flex items-center gap-1">
                                  <XCircle className="w-4 h-4 text-red-600" />
                                  <span className="text-xs text-red-600" title={r.erros.filter((e) => e.includes("stoque") || e.includes("saldo")).join(", ")}>
                                    Ver erros
                                  </span>
                                </span>
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              )}
                            </TableCell>
                            <TableCell>
                              {r.xmlParsed.valorTotal?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoverLinha(idx)}
                                title="Remover da importação"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Erros detalhados */}
                  {resultadosValidacao.some((r) => !r.valido) && (
                    <div className="border rounded-md p-3 bg-yellow-50">
                      <h4 className="font-medium flex items-center gap-1 mb-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-600" /> Detalhes dos erros
                      </h4>
                      <ul className="text-sm space-y-1">
                        {resultadosValidacao
                          .filter((r) => !r.valido)
                          .map((r, i) => (
                            <li key={i}>
                              <strong>Nota {r.xmlParsed.numeroNota || "?"}:</strong>{" "}
                              {r.erros.join("; ")}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {/* Botões de ação */}
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleImportarCorretos}
                      disabled={processando || totalValidos === 0}
                    >
                      {processando ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando...</>
                      ) : (
                        `Importar Corretos (${totalValidos})`
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleImportarCorretosESalvarPendencias}
                      disabled={processando}
                    >
                      Importar Corretos e Salvar Pendências
                    </Button>
                    <Button variant="ghost" onClick={handleReset}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Importando - Barra de Progresso */}
              {etapa === "importando" && progressoLote && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">Processando lote {progressoLote.loteAtual} de {progressoLote.totalLotes}...</span>
                  </div>
                  <Progress value={(progressoLote.loteAtual / progressoLote.totalLotes) * 100} className="h-3" />
                  <p className="text-sm text-muted-foreground">
                    {progressoLote.pedidosCriados} pedido(s) criado(s)
                    {progressoLote.errosAcumulados.length > 0 && ` | ${progressoLote.errosAcumulados.length} erro(s)`}
                  </p>
                </div>
              )}

              {/* Concluído */}
              {etapa === "concluido" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Pedidos criados com status "Aguardando Retorno".</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Vá para a aba Histórico para emitir os retornos simbólicos.</p>
                  <Button onClick={handleReset}>Nova Importação</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ ABA PENDÊNCIAS ═══ */}
        <TabsContent value="pendencias">
          <Card>
            <CardHeader>
              <CardTitle>Pendências de Importação</CardTitle>
            </CardHeader>
            <CardContent>
              {!emitenteId ? (
                <p className="text-muted-foreground">Selecione um emitente acima para ver as pendências.</p>
              ) : (pendencias as any[]).length === 0 ? (
                <p className="text-muted-foreground">Nenhuma pendência encontrada.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nº Nota</TableHead>
                      <TableHead>Chave de Acesso</TableHead>
                      <TableHead>Data Emissão</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(pendencias as any[]).map((p: any) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.numeroNota}</TableCell>
                        <TableCell className="text-xs font-mono">{p.chaveAcesso}</TableCell>
                        <TableCell>{p.dataEmissao}</TableCell>
                        <TableCell className="text-sm">{p.motivo}</TableCell>
                        <TableCell className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => reprocessarPendenciaMutation.mutate({ id: p.id, emitenteId: Number(emitenteId) })}
                            title="Reprocessar"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => descartarPendenciaMutation.mutate({ id: p.id })}
                            title="Descartar"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ ABA HISTÓRICO ═══ */}
        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Histórico de Importações</CardTitle>
                {pedidosAguardandoRetorno > 0 && (
                  <Button
                    onClick={handleEmitirRetornos}
                    disabled={emitindoRetornos}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {emitindoRetornos ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Emitindo...</>
                    ) : (
                      <><Play className="w-4 h-4 mr-2" /> Emitir Retornos ({pedidosAguardandoRetorno})</>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Barra de progresso emissão */}
              {emitindoRetornos && progressoRetornos && (
                <div className="space-y-2">
                  <Progress value={progressoRetornos.restantes > 0 ? ((progressoRetornos.emitidos) / (progressoRetornos.emitidos + progressoRetornos.restantes)) * 100 : 100} className="h-3" />
                  <p className="text-sm text-muted-foreground">
                    {progressoRetornos.emitidos} emitido(s) | {progressoRetornos.restantes} restante(s)
                    {progressoRetornos.erros > 0 && ` | ${progressoRetornos.erros} erro(s)`}
                  </p>
                </div>
              )}

              {!emitenteId ? (
                <p className="text-muted-foreground">Selecione um emitente acima para ver o histórico.</p>
              ) : (historico as any[]).length === 0 ? (
                <p className="text-muted-foreground">Nenhuma importação realizada.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nº Nota</TableHead>
                      <TableHead>Chave de Acesso</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data Importação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(historico as any[]).map((h: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>{h.numeroNota}</TableCell>
                        <TableCell className="text-xs font-mono">{h.chaveAcesso}</TableCell>
                        <TableCell>
                          {Number(h.valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </TableCell>
                        <TableCell>
                          {h.status === "finalizado" ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle className="w-3 h-3" /> Finalizado
                            </span>
                          ) : h.status === "aguardando_retorno" ? (
                            <span className="flex items-center gap-1 text-yellow-600 text-sm">
                              <AlertTriangle className="w-3 h-3" /> Aguardando Retorno
                            </span>
                          ) : (
                            <span className="text-sm">{h.status}</span>
                          )}
                        </TableCell>
                        <TableCell>{h.criadoEm ? new Date(h.criadoEm).toLocaleString("pt-BR") : ""}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
